import React from "react";
import HomeSky, { ShootingStar } from "../Profile/Landing/HomeSky";
import { TeamResource } from "../../stores/TeamStore/Types";

const STAR_LIFETIME = 1600;

const prefersReducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Clicking the sky fires a shooting star, same as the logged-in profile band.
// The starfield reads as interactive whether or not it is, so it may as well
// reward the click rather than swallow it.
const useShootingStars = () => {
  const [stars, setStars] = React.useState<ShootingStar[]>([]);
  const nextId = React.useRef(0);
  const timers = React.useRef<ReturnType<typeof setTimeout>[]>([]);

  React.useEffect(
    () => () => timers.current.forEach((timer) => clearTimeout(timer)),
    [],
  );

  const fire = (event: React.MouseEvent<HTMLElement>) => {
    // Don't hijack clicks meant for the card sitting over the band.
    if ((event.target as HTMLElement).closest(".team-card")) return;
    if (prefersReducedMotion()) return;

    const bounds = event.currentTarget.getBoundingClientRect();
    // Read the pointer off the event *now*. React 16 pools synthetic events, so
    // by the time the setStars updater runs the event has been recycled and
    // clientX/clientY are back to 0 -- which pinned every star to the corner
    // above the band, out of sight, however carefully you aimed.
    const left = event.clientX - bounds.left;
    const top = event.clientY - bounds.top;

    const id = (nextId.current += 1);
    const direction = Math.random() < 0.5 ? -1 : 1;
    const angle = 8 + Math.random() * 20;
    // Same conversion the profile band uses: turning the angle into a real
    // heading, so the half that travel leftwards actually do. Multiplying the
    // angle by the direction only ever aimed right, half of them downwards.
    const radians = (angle * Math.PI) / 180;
    const rotation =
      (Math.atan2(-Math.sin(radians), direction * Math.cos(radians)) * 180) /
      Math.PI;

    setStars((current) =>
      current.concat({
        id,
        left,
        top,
        rotation: `${rotation.toFixed(1)}deg`,
        distance: `${(160 + Math.random() * 120).toFixed(0)}px`,
      }),
    );

    const timer = setTimeout(() => {
      setStars((current) => current.filter((star) => star.id !== id));
      timers.current = timers.current.filter((active) => active !== timer);
    }, STAR_LIFETIME);

    timers.current.push(timer);
  };

  return { stars, fire };
};

interface Props {
  teamResource: TeamResource;
  handleShowSettingsModal: (value: boolean) => void;
}

// Initials stand in when a team has no logo, or the file 404s. Beats a broken
// image icon, and reads as deliberate rather than missing.
const monogram = (name: string) =>
  name
    .replace(/[^\w\s]/g, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

const TeamHeader = ({ teamResource, handleShowSettingsModal }: Props) => {
  const [logoFailed, setLogoFailed] = React.useState(false);
  const showLogo = Boolean(teamResource.logo_url) && !logoFailed;
  const { stars, fire } = useShootingStars();

  return (
    // home-header is the logged-in profile's band -- same plum, same starfield,
    // same centred wordmark -- so the two authenticated landings read as one
    // family rather than two different products.
    <header className="home-header team-header" onClick={fire}>
      <HomeSky shootingStars={stars} />

      <div className="container">
        <span className="logo-center team-header__logo" aria-hidden="true">
          <img
            src={require("../../../../assets/images/landings/footer-logo.svg")}
            alt=""
          />
        </span>

        <div className="team-card">
          <div className="team-card__id">
            <span className="team-card__logo">
              {showLogo ? (
                <img
                  src={teamResource.logo_url}
                  alt={`${teamResource.name} logo`}
                  onError={() => setLogoFailed(true)}
                />
              ) : (
                <span aria-hidden="true">{monogram(teamResource.name)}</span>
              )}
            </span>
            <div className="team-card__meta">
              <div className="team-card__eyebrow">Your team</div>
              <h1 className="team-card__name">{teamResource.name}</h1>
              {teamResource.formatted_created_at && (
                <div className="team-card__since">
                  Member since <b>{teamResource.formatted_created_at}</b>
                </div>
              )}
            </div>
          </div>

          <div className="team-card__stats">
            <div className="team-stat">
              <span className="team-stat__ic" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 3.5a4 4 0 0 1 0 7M22 21v-2a4 4 0 0 0-3-3.8M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM15 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
                </svg>
              </span>
              <span className="team-stat__tx">
                <span className="team-stat__v">{teamResource.user_count}</span>
                <span className="team-stat__l">
                  <span className="lbl-full">Learners</span>
                  <span className="lbl-short">People</span>
                </span>
              </span>
            </div>

            <div className="team-stat">
              <span className="team-stat__ic blue" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" />
                </svg>
              </span>
              <span className="team-stat__tx">
                <span className="team-stat__v">
                  {teamResource.learning_time}
                </span>
                <span className="team-stat__l">
                  <span className="lbl-full">Hours learning</span>
                  <span className="lbl-short">Hours</span>
                </span>
              </span>
            </div>

            <div className="team-stat">
              <span className="team-stat__ic" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="9" r="5" />
                  <path d="M9 13.5 7.5 21l4.5-2.6L16.5 21 15 13.5" />
                </svg>
              </span>
              <span className="team-stat__tx">
                <span className="team-stat__v">
                  {teamResource.certificates_count}
                </span>
                <span className="team-stat__l">
                  <span className="lbl-full">Certificates</span>
                  <span className="lbl-short">Certs</span>
                </span>
              </span>
            </div>
          </div>

          <button
            className="btn btn-secondary btn--sm team-card__settings"
            type="button"
            onClick={() => handleShowSettingsModal(true)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="3" />
              <path d="M19 12a7 7 0 0 0-.1-1l2-1.6-2-3.4-2.4 1a7 7 0 0 0-1.7-1l-.4-2.6h-4l-.4 2.6a7 7 0 0 0-1.7 1l-2.4-1-2 3.4 2 1.6a7 7 0 0 0 0 2l-2 1.6 2 3.4 2.4-1a7 7 0 0 0 1.7 1l.4 2.6h4l.4-2.6a7 7 0 0 0 1.7-1l2.4 1 2-3.4-2-1.6a7 7 0 0 0 .1-1Z" />
            </svg>
            Settings
          </button>
        </div>
      </div>
    </header>
  );
};

export default TeamHeader;
